import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useAxios } from 'hooks';
import { endPoints } from 'api/endpoints';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { useParams } from 'react-router-dom';
import { catalogs } from 'store';
import ProvisioningStyles from 'pages/provisioning/vm/style';
import { sspSubmitOrder, toggleSspModels, costEstimateState, isAppNameValid, isAppShortNameValid, isAppShortNameExist } from './store';
import { resetOrder, formOrderObjFromCart, formOrderObjFromOrderDetails } from './utils/index';

const ProvisioningHeader = React.lazy(() => import('./ProvisioningHeader'));
const ApplicationDetails = React.lazy(() => import('./ApplicationDetails'));
const SspForm = React.lazy(() => import('./SspForm'));
const AddedToCartConfirmModal = React.lazy(() => import('./AddedToCartConfirmModal'));
const UpdatedToCartConfirmModal = React.lazy(() => import('./UpdatedToCartConfirmModal'));
const ShowCostDetails = React.lazy(() => import('./ShowCostDetails'));

const AWSSQSProvisioningPage = () => {
    const [isLoading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const cmpCatalogs = useRecoilValue(catalogs);
    const [, setOrder] = useRecoilState(sspSubmitOrder);
    const resetOrderState = useResetRecoilState(sspSubmitOrder);
    const resetModal = useResetRecoilState(toggleSspModels);
    const resetCost = useResetRecoilState(costEstimateState);
    const resetAppName = useResetRecoilState(isAppNameValid);
    const resetShortName = useResetRecoilState(isAppShortNameValid);
    const resetShortNameExist = useResetRecoilState(isAppShortNameExist);
    const { catalogCode, cartItemId, operation, catalogServiceId } = useParams();
    const axios = useAxios();

    React.useEffect(() => {
        let active = true;
        const checkCatalog = async () => {
            if (!cmpCatalogs?.length || !catalogCode) return;
            setLoading(true);
            setError('');
            const catalog = cmpCatalogs.find((item) => item?.catalogDefinitionId === catalogCode);
            if (!catalog) {
                setError('The SQS catalog is not available.');
                setLoading(false);
                return;
            }
            try {
                const { data: costs } = await axios.get(`${endPoints.catalog.get}/${catalogCode}/costs`);
                let restored = {};
                if (cartItemId && operation && !catalogServiceId) {
                    const { data } = await axios.get(`${endPoints.cart.getCartItem}/${cartItemId}`);
                    if (!data?.itemDetail?.catalogService) throw new Error('The SQS cart item was not found.');
                    restored = formOrderObjFromCart(data);
                } else if (cartItemId && operation && catalogServiceId) {
                    const { data } = await axios.get(`${endPoints.order.orderDetails}/${cartItemId}`);
                    if (!data?.catalogServices?.some((item) => item?.catalogServiceId === catalogServiceId)) {
                        throw new Error('The SQS order was not found.');
                    }
                    restored = formOrderObjFromOrderDetails(catalogServiceId, data);
                }
                if (active) {
                    setOrder({
                        ...resetOrder,
                        catalogCode,
                        catalogType: catalog?.displayName,
                        cloudProvider: catalog?.cloudProvider,
                        cloudService: catalog?.cloudService,
                        sku: catalog?.images?.sku,
                        ...restored,
                        emptyCost: costs,
                    });
                }
            } catch (loadError) {
                if (active) setError(loadError.message || 'Unable to load the SQS provisioning form.');
            } finally {
                if (active) setLoading(false);
            }
        };
        checkCatalog();
        return () => {
            active = false;
        };
    }, [cmpCatalogs, catalogCode, cartItemId, operation, catalogServiceId]);

    React.useEffect(
        () => () => {
            resetOrderState();
            resetModal();
            resetCost();
            resetAppName();
            resetShortName();
            resetShortNameExist();
        },
        []
    );

    const loading = (
        <Box sx={ProvisioningStyles.provisioning_wrapper}>
            <CircularProgress color="inherit" size={16} sx={ProvisioningStyles.circularProgressColor} />
            <Typography variant="body2" sx={ProvisioningStyles.circularProgressColor}>
                Loading
            </Typography>
        </Box>
    );

    return (
        <>
            <Helmet>
                <title>AWS SQS Provisioning</title>
            </Helmet>
            <Suspense fallback={loading}>
                {isLoading && loading}
                {error && <Alert severity="error">{error}</Alert>}
                {!isLoading && !error && (
                    <Box sx={ProvisioningStyles.provisioningBox}>
                        <ProvisioningHeader />
                        <Box sx={ProvisioningStyles.sspform_wrapper} className="contentContainer">
                            <SspForm />
                        </Box>
                        <br />
                        <ApplicationDetails />
                        <AddedToCartConfirmModal />
                        <UpdatedToCartConfirmModal />
                        <ShowCostDetails />
                    </Box>
                )}
            </Suspense>
        </>
    );
};

export default React.memo(AWSSQSProvisioningPage);
